import { createContext, useContext } from 'react'
import { supabase } from './supabase'

/**
 * 165 (David, 11 Oct). The OneConeTec terms of use, agreed on first sign-in,
 * and the Star One program terms, agreed before any star activity is shown.
 *
 * ── WHERE THE TRUTH LIVES ───────────────────────────────────────────────────
 *
 * The words, the versions and who agreed are in the database
 * (terms_document, terms_document_variant, terms_acceptance) and are reached
 * only through three functions, each about the caller: my_terms(),
 * terms_text(kind, locale) and accept_terms(document, locale). There is no
 * copy of the terms in this bundle — a new version is a migration, and every
 * reader is asked again because the CURRENT document changed.
 *
 * ── WHAT IS ENFORCED WHERE ──────────────────────────────────────────────────
 *
 * The platform terms are a CLIENT gate (TermsGate). Star One is both: the
 * cards and the page show the agreement instead of stars, and the database
 * refuses a recognition carrying stars, or a shop order, from somebody who
 * has not agreed. Stars ARRIVING are never refused — "stars wait for them".
 */
export type TermsKind = 'platform' | 'star_one'

export type TermsStatus = {
  kind: TermsKind
  documentId: string
  version: number
  effectiveAt: string
  accepted: boolean
  acceptedAt: string | null
}

export type TermsText = {
  documentId: string
  kind: TermsKind
  version: number
  effectiveAt: string
  /** The locale actually returned: es-CL only while the Spanish matches its English. */
  locale: string
  title: string
  body: string
  translationStatus: 'approved' | 'machine'
  balanceLimit: number
  expiryMonths: number
}

export async function loadMyTerms(): Promise<TermsStatus[]> {
  const { data, error } = await supabase.rpc('my_terms')
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    kind: r.kind as TermsKind,
    documentId: r.document_id as string,
    version: r.version as number,
    effectiveAt: r.effective_at as string,
    accepted: Boolean(r.accepted),
    acceptedAt: (r.accepted_at as string | null) ?? null,
  }))
}

/** Null when the tenant has no document of that kind. */
export async function loadTermsText(kind: TermsKind, locale?: string): Promise<TermsText | null> {
  const { data, error } = await supabase.rpc('terms_text', locale ? { p_kind: kind, p_locale: locale } : { p_kind: kind })
  if (error) throw error
  const r = (data ?? [])[0]
  if (!r) return null
  return {
    documentId: r.document_id,
    kind: r.kind,
    version: r.version,
    effectiveAt: r.effective_at,
    locale: r.locale,
    title: r.title,
    body: r.body,
    translationStatus: r.translation_status,
    balanceLimit: r.balance_limit,
    expiryMonths: r.expiry_months,
  }
}

/** Records the caller's agreement to `documentId`, read in `locale`. */
export async function acceptTerms(documentId: string, locale: string): Promise<string> {
  const { data, error } = await supabase.rpc('accept_terms', { p_document: documentId, p_locale: locale })
  if (error) throw error
  return data as string
}

/** What the gate decided about the platform terms, from my_terms(). */
export function platformDecision(rows: TermsStatus[]): 'agreed' | 'needed' {
  const p = rows.find((r) => r.kind === 'platform')
  // No platform document in this tenant asks nobody — the server's rule too.
  return !p || p.accepted ? 'agreed' : 'needed'
}

/** The same for Star One. */
export function starOneDecision(rows: TermsStatus[]): 'agreed' | 'needed' {
  const s = rows.find((r) => r.kind === 'star_one')
  return !s || s.accepted ? 'agreed' : 'needed'
}

// ── One block of terms text ─────────────────────────────────────────────────
// The body is plain text: "## " starts a heading, "- " a bullet, a blank line
// a paragraph (165's column comment). Parsed here into blocks so nothing is
// ever rendered as HTML.
export type TermsBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'para'; text: string }
  | { kind: 'list'; items: string[] }

export function parseTermsBody(body: string): TermsBlock[] {
  const out: TermsBlock[] = []
  let para: string[] = []
  let list: string[] | null = null
  const flushPara = () => {
    if (para.length) out.push({ kind: 'para', text: para.join(' ') })
    para = []
  }
  const flushList = () => {
    if (list && list.length) out.push({ kind: 'list', items: list })
    list = null
  }
  for (const raw of body.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trim()
    if (line === '') {
      flushPara()
      flushList()
    } else if (line.startsWith('## ')) {
      flushPara()
      flushList()
      out.push({ kind: 'heading', text: line.slice(3).trim() })
    } else if (line.startsWith('- ')) {
      flushPara()
      ;(list ??= []).push(line.slice(2).trim())
    } else {
      flushList()
      para.push(line)
    }
  }
  flushPara()
  flushList()
  return out
}

// ── Star One's state, for every component under the gate ───────────────────
export type StarOneState = {
  /** 'unknown' while loading or after a failed read: show nothing of stars. */
  status: 'agreed' | 'needed' | 'unknown'
  /** Re-read my_terms() — after agreeing, from wherever it happened. */
  refresh: () => Promise<void>
  /** 176. Only while an administrator views as somebody who has not joined:
   *  step past the agreement to see the page as it would be. Joins nobody. */
  previewPast?: () => void
}

/**
 * The default is AGREED, so a component rendered with no provider — which is
 * everything the web suite mounts — draws what it drew before 165. The gate
 * always provides the real value.
 */
export const StarOneContext = createContext<StarOneState>({
  status: 'agreed',
  refresh: async () => {},
})

export function useStarOne(): StarOneState {
  return useContext(StarOneContext)
}
